import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';

export const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

let scriptPromise = null;
function loadScript() {
  if (window.turnstile) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      s.async = true;
      s.onload = resolve;
      s.onerror = (e) => {
        // Allow a later mount (or navigation back) to retry the load instead of
        // being stuck with a permanently-rejected promise (CDN blip, adblocker).
        scriptPromise = null;
        reject(e);
      };
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
}

// Cloudflare Turnstile widget. Renders nothing when no sitekey is configured
// (REACT_APP_TURNSTILE_SITE_KEY), so the app works without it in dev.
//
// Tokens are SINGLE-USE and expire ~5 min after issuance. In invisible mode the
// widget auto-solves on load, so a user who fills the form slowly — or who
// retries after any failure — can end up submitting a stale/consumed token,
// which the backend rejects with "Captcha verification required/failed".
//
// To make this bulletproof, the parent should call `ref.getToken()` AT SUBMIT
// TIME: it returns a guaranteed-fresh, not-yet-consumed token (refreshing the
// widget if the cached one is old or already used). Returns '' if no token can
// be obtained (script blocked, challenge abandoned) so the caller can show a
// retry message instead of hanging.
const Turnstile = forwardRef(function Turnstile({ onVerify }, apiRef) {
  const ref = useRef(null);
  const widgetId = useRef(null);
  const cb = useRef(onVerify);
  cb.current = onVerify;
  // Cached token + freshness/consumption bookkeeping.
  const tokenState = useRef({ token: '', consumed: true, issuedAt: 0 });
  // Resolver for an in-flight getToken() awaiting the next solve.
  const pending = useRef(null);
  // Set when the Cloudflare script can't be loaded at all (adblocker / network).
  const scriptFailed = useRef(false);

  const settlePending = (token) => {
    if (pending.current) { const p = pending.current; pending.current = null; p(token); }
  };
  const deliver = (token) => {
    tokenState.current = { token, consumed: false, issuedAt: Date.now() };
    cb.current?.(token);
    settlePending(token);
  };
  const clearToken = () => {
    tokenState.current = { token: '', consumed: true, issuedAt: 0 };
    cb.current?.('');
  };

  useImperativeHandle(apiRef, () => ({
    scriptBlocked() { return scriptFailed.current; },
    reset() {
      clearToken();
      if (widgetId.current !== null && window.turnstile) {
        try { window.turnstile.reset(widgetId.current); } catch { /* widget gone */ }
      }
    },
    // Returns a fresh, single-use token (or '' if none can be obtained).
    async getToken() {
      if (!TURNSTILE_SITE_KEY) return '';
      if (scriptFailed.current) return '';
      const st = tokenState.current;
      const fresh = st.token && !st.consumed && (Date.now() - st.issuedAt < 240000);
      if (fresh) { st.consumed = true; return st.token; }
      // A getToken() is already in flight — chain onto the same waiter so a
      // double-click can't orphan the first caller's promise.
      if (pending.current) {
        return new Promise((resolve) => {
          const prev = pending.current;
          pending.current = (t) => { prev(t); resolve(t); };
        });
      }
      // Need a new one: re-run the challenge and wait for the callback. The
      // timeout is generous because Cloudflare can show an INTERACTIVE challenge
      // that a real human needs time to solve — too short a timeout would cancel
      // it mid-solve and lock the user out.
      if (widgetId.current !== null && window.turnstile) {
        try { window.turnstile.reset(widgetId.current); } catch { /* will rely on initial render */ }
      }
      const token = await new Promise((resolve) => {
        pending.current = resolve;
        setTimeout(() => { if (pending.current === resolve) { pending.current = null; resolve(''); } }, 90000);
      });
      if (token) tokenState.current = { token, consumed: true, issuedAt: Date.now() };
      return token;
    },
  }));

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return undefined;
    let cancelled = false;
    loadScript().then(() => {
      if (cancelled || !ref.current || widgetId.current !== null) return;
      widgetId.current = window.turnstile.render(ref.current, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: 'dark',
        'refresh-expired': 'auto',
        'retry': 'auto',
        callback: (token) => deliver(token),
        'expired-callback': () => clearToken(),
        'error-callback': () => { clearToken(); settlePending(''); },
      });
    }).catch(() => {
      // Script blocked/unreachable: fail fast so getToken() doesn't hang, and
      // unblock any in-flight waiter immediately.
      scriptFailed.current = true;
      settlePending('');
    });
    return () => {
      cancelled = true;
      // Unblock a getToken() awaiting at unmount so its caller exits cleanly.
      settlePending('');
      if (widgetId.current !== null && window.turnstile) {
        try { window.turnstile.remove(widgetId.current); } catch { /* already gone */ }
        widgetId.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!TURNSTILE_SITE_KEY) return null;
  return <div ref={ref} className="flex justify-center min-h-[1px]" data-testid="turnstile-widget" />;
});

export default Turnstile;
